'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import { HEADER_HEIGHT } from '@/components/Header/constants';
import { ALGOLIA_MAX_SEARCH_STRING_LENGTH } from '@/features/algolia/constants';
import { portalContainerId } from '@/components/Portal/PortalContainer';
import { GothamBook } from '@/utils/fonts';
import Icon from '@/components/Icon/Icon';

// TODO - replace image imports with Contentful media

type TSearchProps = {
    handleSearch?: (val: string) => void;
    className?: string;
    maxInputLength?: number;
    searchLabel?: string;
};

export const HeaderSearch = ({
    handleSearch,
    className = '',
    maxInputLength = ALGOLIA_MAX_SEARCH_STRING_LENGTH,
    searchLabel = 'Search',
}: TSearchProps) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isExpanding, setIsExpanding] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const mobileSearchContainerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<Element | null>(null);
    useEffect(() => {
        modalRef.current = document.getElementById(portalContainerId);
    }, []);

    const handleOpenSearch = () => {
        setIsSearchOpen(true);
        setTimeout(() => {
            setIsExpanding(true);
        }, 10);
    };

    const handleCloseSearch = useCallback(() => {
        setIsExpanding(false);
        setIsClosing(true);
        setSearchValue('');
        setTimeout(() => {
            setIsSearchOpen(false);
            setIsClosing(false);
        }, 200); // Match the duration with animation duration
    }, [setIsExpanding, setIsClosing, setIsSearchOpen]);

    const handleClick = () => {
        if (isSearchOpen) {
            handleCloseSearch();
        } else {
            handleOpenSearch();
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isSearchOpen &&
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target as Node) &&
                mobileSearchContainerRef.current &&
                !mobileSearchContainerRef.current.contains(event.target as Node)
            ) {
                handleCloseSearch();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleCloseSearch, isSearchOpen, searchContainerRef]);

    const handleSubmit = (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => {
        e.stopPropagation();
        handleSearch?.(searchValue);
        handleCloseSearch();
    };

    return (
        <div className={className} ref={searchContainerRef}>
            <div className="tw-hidden lg:tw-block">
                <Icon
                    type="search"
                    className={classNames('search-icon tw-float-right tw-cursor-pointer', {
                        'tw-hidden': isSearchOpen,
                        'tw-block': !isSearchOpen,
                    })}
                    onClick={handleClick}
                />
                {isSearchOpen && (
                    <div
                        className={classNames(
                            'tw-overflow-hidden tw-translate-x-full tw-float-right',
                            {
                                'tw-animate-search-appear-from-right': isExpanding && !isClosing,
                                'tw-animate-search-hide-to-right': isClosing && !isExpanding,
                                'tw-hidden': isSearchOpen && !isExpanding && !isClosing,
                            },
                        )}
                    >
                        <div
                            className={classNames(
                                'tw-flex tw-flex-row tw-items-center tw-gap-2 tw-border-b tw-pb-1',
                            )}
                        >
                            <Icon
                                type="search"
                                className="search-icon tw-cursor-pointer"
                                onClick={handleClick}
                            />
                            <input
                                className="tw-border-0 tw-outline-none tw-max-w-[100px] tw-placeholder-textBase"
                                onChange={(e) => setSearchValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSubmit(e);
                                    }
                                }}
                                value={searchValue}
                                maxLength={maxInputLength}
                                type="text"
                                placeholder={searchLabel}
                            />
                            <Icon
                                type="angleRight"
                                className="tw-fill-pink1 tw-cursor-pointer"
                                onClick={handleSubmit}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="lg:tw-hidden tw-block">
                <Icon type="search" onClick={handleClick} className="tw-fill-textBase tw-cursor-pointer" />
                {isSearchOpen && (
                    <div
                        className="lg:tw-hidden tw-block tw-overflow-hidden tw-w-full tw-absolute tw-left-0 tw-h-[70px] tw-z-[100]"
                        style={{ top: `${HEADER_HEIGHT - 1}px` }}
                        ref={mobileSearchContainerRef}
                    >
                        <div
                            className={classNames(
                                'tw-w-full tw-overflow-hidden tw-absolute tw-bg-white tw-px-[15px] tw-py-[10px] tw-border-y',
                                {
                                    'tw-animate-search-appear-from-top': isExpanding && !isClosing,
                                    'tw-animate-search-hide-to-top': isClosing && !isExpanding,
                                    'tw-hidden': isSearchOpen && !isExpanding && !isClosing,
                                },
                            )}
                        >
                            <div
                                className={classNames(
                                    'tw-flex tw-flex-row tw-items-center tw-gap-2',
                                )}
                            >
                                <Icon
                                    type="search"
                                    className="search-icon tw-cursor-pointer tw-fill-menuText"
                                    onClick={handleClick}
                                />
                                <input
                                    className={classNames(
                                        'tw-border-0 tw-outline-none tw-grow tw-text-sm tw-ml-3 tw-px-[8px] tw-py-[10px] tw-rounded-none tw-transition-shadow focus:tw-shadow-searchHighlight tw-placeholder-gray-600',
                                        GothamBook.className,
                                    )}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSubmit(e);
                                        }
                                    }}
                                    value={searchValue}
                                    maxLength={maxInputLength}
                                    placeholder={searchLabel}
                                    type="text"
                                />
                                <Icon
                                    type="angleRight"
                                    width={28}
                                    height={28}
                                    className="tw-fill-pink1 tw-cursor-pointer tw-border-r"
                                    onClick={handleSubmit}
                                />
                                <Icon
                                    type="close"
                                    height={24}
                                    width={24}
                                    className="tw-cursor-pointer tw-fill-menuText"
                                    onClick={handleCloseSearch}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
